import moment from 'moment';
import { actionCreators } from '../../store/actions';
import ReduxActions from '../../store/ReduxActions';
import { AverageOrderPreparationTimeInterface, EventHelperActionInterface, FormatCurrencyOptions } from './Interfaces';
import { collect } from 'collect.js';

class HelperMethods {
  triggerEventSideEffects = (eventAction: EventHelperActionInterface) => {
    ReduxActions.dispatch(actionCreators.eventHelper.setEvent(eventAction));
  };

  resetEventSideEffects = () => {
    ReduxActions.dispatch(actionCreators.eventHelper.resetEvent());
  };

  formatCurrency(amount: number, options: FormatCurrencyOptions): string | number {
    const { currency, locale, trimInteger = false } = options;

    // A menu item can paint before the company payload lands, and the currency comes from it
    // (AppMethodsProvider). `Intl.NumberFormat` throws outright on a missing currency under
    // `style: 'currency'`, which reaches no boundary but the app-wide one — a white screen
    // instead of an unformatted price. Returning the bare amount degrades that to a cosmetic
    // gap for the one render before the payload arrives.
    //
    // Deliberately NOT a defaulted currency: the 'EUR' default this replaces was what hid the
    // crash, and it silently prices a US or GB shop in euros.
    if (!currency) {
      return amount;
    }

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: Number.isInteger(amount) && trimInteger ? 0 : undefined,
    }).format(amount);
  }

  getCurrencySymbol(currency: string, locale: string): string {
    try {
      return (
        new Intl.NumberFormat(locale, { style: 'currency', currency })
          .formatToParts(0)
          .find((part) => part.type === 'currency')?.value ?? currency
      );
    } catch {
      return currency;
    }
  }

  // Adyen Web ships translations for exactly these tags and silently falls back to its own
  // default for anything else, so resolve the closest one from the region's own number_locale
  // rather than handing it a bare 'de' / 'th'. The region block is optional on the company
  // payload (and absent until it lands), so a missing locale resolves to Adyen's own default.
  resolveAdyenLocale(numberLocale?: string): string {
    if (!numberLocale) {
      return 'en-US';
    }

    const supported = [
      'cs-CZ',
      'da-DK',
      'de-DE',
      'el-GR',
      'en-US',
      'es-ES',
      'fi-FI',
      'fr-FR',
      'hr-HR',
      'hu-HU',
      'it-IT',
      'ja-JP',
      'ko-KR',
      'nl-NL',
      'no-NO',
      'pl-PL',
      'pt-BR',
      'pt-PT',
      'ro-RO',
      'ru-RU',
      'sk-SK',
      'sl-SI',
      'sv-SE',
      'zh-CN',
      'zh-TW',
    ];
    const [rawLang, country] = numberLocale.split('-');
    const lang = ({ nb: 'no', nn: 'no' } as Record<string, string>)[rawLang] ?? rawLang;
    const exact = `${lang}-${country ?? ''}`;

    if (supported.includes(exact)) {
      return exact;
    }

    return supported.find((tag) => tag.startsWith(`${lang}-`)) ?? 'en-US';
  }

  // The backend's date_format/hour_cycle are CLDR/Unicode tokens (meaningful to any client);
  // moment wants its own token set. Uppercase only the y/d runs — M/H/h/m/s already agree
  // between the two dialects.
  cldrToMoment(pattern?: string): string {
    return (pattern ?? '')
      .replace(/y+/g, (match) => match.toUpperCase())
      .replace(/d+/g, (match) => match.toUpperCase());
  }

  // h23 -> 24-hour clock, h12 -> 12-hour clock with meridiem — moment and CLDR agree on the
  // token shape here, so this isn't routed through cldrToMoment().
  momentTimeFormat(hourCycle: 'h12' | 'h23'): string {
    return hourCycle === 'h12' ? 'h:mm a' : 'HH:mm';
  }

  ceilNumberWithPrecision(number: number, decimalPlaces: number = 2) {
    const factorOfTen = Math.pow(10, decimalPlaces);

    return Math.ceil(number * factorOfTen) / factorOfTen;
  }

  roundNumberWithPrecision(number: number, decimalPlaces: number = 2) {
    const factorOfTen = Math.pow(10, decimalPlaces);

    return Math.round(number * factorOfTen) / factorOfTen;
  }

  getDay = (day?: number): string => moment().set({ day }).format('dddd');

  formatDistance(meters: number): string {
    if (!meters) {
      return null;
    }

    if (meters > 500) {
      return `${Math.round(meters / 1000).toFixed(1)} km`;
    }

    return `${meters.toFixed(0)} m`;
  }

  formatTimeInterval(interval: AverageOrderPreparationTimeInterface) {
    if (interval?.max) {
      return `${interval?.min}-${interval?.max}`;
    }

    return `${interval?.min}`;
  }

  public getNextDayOfWeek(dayOfWeek: number): number {
    return dayOfWeek === 6 ? 0 : ++dayOfWeek;
  }

  public getPreviousDayOfWeek(dayOfWeek: number): number {
    return dayOfWeek === 0 ? 6 : --dayOfWeek;
  }

  public min(...values: any[]): number {
    return collect(values)
      .filter((item) => !!item)
      .min();
  }
}

export default new HelperMethods();
